'use strict';

/**
 * Cart Submit and pay helpers (London Smoke & Cure).
 * Prepaid markers for OrderDesk + safe new-tab open for Xero Online Invoice.
 */

/** OrderDesk payment_type label for advance checkout pay. */
export const ORDER_DESK_PREPAID_PAYMENT_TYPE = 'Prepaid';

/**
 * OrderDesk payment_status must be one of the API enum values.
 * Approved = payment accepted / prepaid intent for ops.
 */
export const ORDER_DESK_PREPAID_PAYMENT_STATUS = 'Approved';

export const ORDER_DESK_PREPAID_CHECKOUT_STATUS = 'Prepaid';

/**
 * Fields to merge onto an order when the customer chose Submit and pay.
 * Safe for Firestore + OrderDesk (payment_* are OD API fields).
 */
export function buildPrepaidOrderFields() {
  return {
    payment_type: ORDER_DESK_PREPAID_PAYMENT_TYPE,
    payment_status: ORDER_DESK_PREPAID_PAYMENT_STATUS,
    checkout_data: {
      Payment: ORDER_DESK_PREPAID_CHECKOUT_STATUS,
      'Payment Status': ORDER_DESK_PREPAID_CHECKOUT_STATUS,
    },
    // Firestore / ops markers (stripped from OD if listed as firestore-only later)
    payInAdvance: true,
    paymentIntent: 'prepaid',
  };
}

/**
 * Merge prepaid markers into an order payload (shallow checkout_data merge).
 */
export function applyPrepaidMarkersToOrder(order = {}) {
  const prepaid = buildPrepaidOrderFields();
  return {
    ...order,
    payment_type: prepaid.payment_type,
    payment_status: prepaid.payment_status,
    payInAdvance: true,
    paymentIntent: 'prepaid',
    checkout_data: {
      ...(order.checkout_data || {}),
      ...prepaid.checkout_data,
    },
  };
}

/**
 * Open Xero hosted pay URL in a new tab. Never navigates the cart tab away.
 * @returns {{ opened: boolean, blocked: boolean, url: string }}
 */
export function openPayUrlInNewTab(url) {
  const href = String(url || '').trim();
  if (!href) {
    return { opened: false, blocked: false, url: '' };
  }
  let win = null;
  try {
    win = window.open(href, '_blank', 'noopener,noreferrer');
  } catch {
    win = null;
  }
  if (win) {
    try {
      win.opener = null;
    } catch {
      /* ignore */
    }
    return { opened: true, blocked: false, url: href };
  }
  return { opened: false, blocked: true, url: href };
}

/**
 * Customer-sanitised Submit and pay failure for cart overlay.
 * Never echo Admin paths, .env keys, kill switch, or raw backend/Xero ops text.
 */
export function formatSubmitAndPayError(err, fallback) {
  const code = err?.code || '';
  if (code === 'pay_now_not_offered' || code === 'master_off' || code === 'kill_switch') {
    return 'Pay now is not available for this order. Your order is confirmed — pay later from Account.';
  }
  if (err?.reason === 'excluded_sku' || code === 'excluded_sku') {
    return 'This order includes items that must be weighed before payment. Your order is confirmed — pay later from Account.';
  }
  if (code === 'xero_rate_limit') {
    return 'Payment is busy right now. Your order is confirmed — open Pay now from Account in a few minutes.';
  }
  if (code === 'no_contact_hints' || code === 'contact_not_found') {
    return 'We could not set up payment for your account yet. Your order is confirmed — pay later from Account or contact us.';
  }
  return (
    fallback ||
    'Could not open payment. Your order is confirmed — you can pay later from Account.'
  );
}
